import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { nanoid } from "nanoid";
import { serverTimestamp, doc, setDoc, onSnapshot, query, collection, where } from "firebase/firestore";
import { Building2, Plus, X } from "lucide-react";

import { auth, db } from "../firebase.js";
import Button from "./Button.js";

function Modal({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div role="dialog" aria-modal="true" className="relative bg-bg-surface rounded-lg shadow-xl max-w-modal w-full animate-slide-up">
        {children}
      </div>
    </div>
  );
}

function labelOf(item) {
  return item.name || item.bedrijfsnaam || "...";
}

function VenueSelect(props) {
  const [venues, setVenues] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [openNewVenueModal, setOpenNewVenueModal] = useState(false);
  const hasPreloadedFirstItem = useRef(false);

  const navigate = useNavigate();
  const includeServices = !!props.includeServices;

  const combined = [...venues, ...services];
  const extraVenue = props.activeVenue
    && !combined.some((v) => v.objectID === props.activeVenue.objectID)
    ? props.activeVenue
    : null;

  useEffect(() => {
    const q = query(collection(db, "locations"), where("uid", "==", auth.currentUser.uid));
    return onSnapshot(q, (snapshot) => {
      setVenues(snapshot.docs.map((doc) => ({ objectID: doc.id, ...doc.data(), listingType: "venue" })));
    });
  }, []);

  useEffect(() => {
    if (!includeServices) return undefined;
    const q = query(collection(db, "services"), where("uid", "==", auth.currentUser.uid));
    return onSnapshot(q, (snapshot) => {
      setServices(snapshot.docs.map((doc) => ({ objectID: doc.id, ...doc.data(), listingType: "service" })));
    });
  }, [includeServices]);

  // Auto-select considers venues + diensten so a dienst-only owner still gets preloaded.
  useEffect(() => {
    const items = [...venues, ...services];
    const shouldAutoSelect = items.length === 1 || (props.preloadFirstItem && items.length > 0);
    if (shouldAutoSelect && !hasPreloadedFirstItem.current && !props.activeVenueId) {
      hasPreloadedFirstItem.current = true;
      props.onChange(items[0].objectID, items[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues, services, props.preloadFirstItem, props.activeVenueId, props.onChange]);

  async function onChange(e) {
    const all = [...venues, ...services, ...(extraVenue ? [extraVenue] : [])];
    const selected = _.find(all, ["objectID", e.target.value]);
    props.onChange(e.target.value, selected);
  }

  async function onCreate(e) {
    setLoading(true);

    try {
      const objectID = nanoid(10).toLowerCase();
      await setDoc(doc(db, "locations", objectID), {
        published: false,
        uid: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        name,
      });
      navigate(`/venues/${objectID}`);
    } catch (err) {
      console.log(err);
    }

    setName("");
    setLoading(false);
    setOpenNewVenueModal(false);
  }

  return (
    <>
      <Modal open={openNewVenueModal} onClose={() => setOpenNewVenueModal(false)}>
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-xl font-heading font-bold">Nieuwe locatie</h3>
          <button
            onClick={() => setOpenNewVenueModal(false)}
            aria-label="Sluiten"
            className="p-1.5 rounded-sm text-text-tertiary hover:text-text-primary hover:bg-bg-sunken transition-colors duration-fast"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6">
          <label className="label-base">Naam</label>
          <input
            type="text"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input-base"
            placeholder="Naam van de locatie"
          />
        </div>
        <div className="flex justify-end gap-3 p-6 pt-0">
          <Button variant="secondary" label="Annuleren" onClick={() => setOpenNewVenueModal(false)} />
          <Button pending={loading} loadingLabel="Even wachten..." label="Opslaan" onClick={() => onCreate()} />
        </div>
      </Modal>

      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Building2
            size={16}
            strokeWidth={1.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
          />
          {(venues.length > 0 || services.length > 0 || extraVenue) && (
            <select value={props.activeVenueId || ""} onChange={onChange} className="input-base pl-9 pr-8 appearance-none cursor-pointer">
              <option value="">{includeServices ? "Selecteer locatie of dienst" : "Selecteer locatie"}</option>
              {extraVenue && (
                <option key={extraVenue.objectID} value={extraVenue.objectID}>
                  {labelOf(extraVenue)}
                </option>
              )}
              {includeServices ? (
                <>
                  {venues.length > 0 && (
                    <optgroup label="Locaties">
                      {_.map(venues, (venue) => (
                        <option key={venue.objectID} value={venue.objectID}>
                          {labelOf(venue)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {services.length > 0 && (
                    <optgroup label="Diensten">
                      {_.map(services, (service) => (
                        <option key={service.objectID} value={service.objectID}>
                          {labelOf(service)}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </>
              ) : (
                _.map(venues, (venue) => (
                  <option key={venue.objectID} value={venue.objectID}>
                    {labelOf(venue)}
                  </option>
                ))
              )}
            </select>
          )}
        </div>
        {props.showNewButton && (
          <Button variant="secondary" size="default" label="Nieuwe locatie" icon={Plus} onClick={() => setOpenNewVenueModal(true)} />
        )}
      </div>

    </>
  );
}

export default VenueSelect;
